import { computed, inject, Injectable, signal, Signal } from "@angular/core";
import { DOCUMENT } from "@angular/common";
import { toSignal } from "@angular/core/rxjs-interop";
import { subMonths, format } from "date-fns";
import { fromEvent, merge, timer } from "rxjs";
import { filter, map, scan } from "rxjs/operators";

import { LogService } from "@bitwarden/common/abstractions/log.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";

// Single source of truth for the "month key" format used to persist/compare lastSeenSummaryMonth.
export const monthKey = (date: Date): string => format(date, "yyyy-MM");

@Injectable({ providedIn: "root" })
export class MonthlySummaryVisibilityService {
  private readonly userStore = inject(UserStoreService);
  private readonly log = inject(LogService);
  private readonly _document = inject(DOCUMENT);

  // True after the user dismisses the summary in this session. Prevents the summary from
  // reappearing if the network save fails — the next session will show it again if the
  // save never persisted.
  private readonly _dismissedThisSession = signal(false);

  // Flips to true 10 s after service init if preferences are still null (network failure,
  // auth error). At that point we treat the session as a new-user path and show the dialog
  // rather than deferring indefinitely with no user-visible indication.
  private readonly _prefLoadTimedOut = toSignal(timer(10_000).pipe(map(() => true)), {
    initialValue: false,
  });

  // Fires at midnight (24 h timer) OR whenever the page becomes visible after the device
  // wakes from sleep — either trigger re-evaluates shouldShowSummary for the correct month.
  // scan() converts each emission into a unique incrementing counter so the signal always
  // receives a new value, even when visibilitychange fires multiple times in the same session.
  // Constraint: initialValue must not equal (scan seed + 1) — i.e. must differ from the first
  // scan output — otherwise computed() would not detect a value change on the first tick.
  //   scan seed = -1  →  first output = 0
  //   initialValue = -2  ≠  0  →  value change detected on first tick  →  computed() re-runs ✓
  private readonly _clockTick = toSignal(
    merge(
      timer(MonthlySummaryVisibilityService._msUntilMidnight(), 24 * 60 * 60 * 1000),
      fromEvent(this._document, "visibilitychange").pipe(filter(() => !this._document.hidden)),
    ).pipe(scan((acc) => acc + 1, -1)),
    { initialValue: -2 },
  );

  private static _msUntilMidnight(): number {
    const now = new Date();
    const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    return midnight.getTime() - now.getTime();
  }

  // Single source of truth for the "last month" key — recomputes only when _clockTick fires
  // (once at midnight or page wake). Shared between shouldShowSummary and markSummaryAsSeen
  // so that both operate on the same key within a tick window.
  private readonly _lastMonthKeySignal: Signal<string> = computed(() => {
    void this._clockTick(); // registers _clockTick as a reactive dependency — do not remove
    return monthKey(subMonths(new Date(), 1));
  });

  // Intentional: undefined lastSeenSummaryMonth means the user has never seen this feature,
  // including all existing users on the first deploy. Showing the summary to everyone on
  // first launch is the intended product behavior.
  //
  // When pref is null the dialog is deliberately deferred (not suppressed): preferences are
  // still loading and we don't yet know whether the user has seen this month's summary.
  // Showing the dialog immediately while pref is null would cause it to flash open and then
  // instantly close once a returning user's preferences load and confirm the month matches.
  // The computed() re-runs automatically when preferenceView() emits its first non-null value,
  // so the dialog still appears for new users — just after the first preferences snapshot arrives.
  readonly shouldShowSummary: Signal<boolean> = computed(() => {
    if (this._dismissedThisSession()) {
      return false;
    }
    const pref = this.userStore.preferences.preferenceView();
    if (!pref) {
      if (this._prefLoadTimedOut()) {
        // Preferences still null after 10 s — network or auth issue. Treat as new-user path
        // so the dialog is not suppressed indefinitely.
        this.log.warning(
          "MonthlySummaryVisibilityService: preferences still null after 10 s — showing summary on new-user path",
        );
        return true;
      }
      // Preferences not yet loaded — defer until they arrive.
      return false;
    }
    // undefined lastSeenSummaryMonth → user has never seen this feature → show.
    return pref.lastSeenSummaryMonth !== this._lastMonthKeySignal();
  });

  // seenMonthKey: the month the caller was actually displaying when the user dismissed it —
  // callers should always pass this explicitly (derived from the month they opened) rather
  // than rely on the fallback below, since this signal only updates on a clock tick and can be
  // stale relative to what's on screen.
  async markSummaryAsSeen(seenMonthKey?: string): Promise<void> {
    // Immediately suppress the summary for this session so the user's dismiss action takes
    // effect even if the network save below fails.
    this._dismissedThisSession.set(true);

    const monthKey = seenMonthKey ?? this._lastMonthKeySignal();
    const pref = this.userStore.preferences.preferenceView()?.clone();
    if (!pref) {
      return;
    }
    pref.lastSeenSummaryMonth = monthKey;
    try {
      await this.userStore.preferences.save(pref);
    } catch (e) {
      // Non-fatal: _dismissedThisSession prevents re-showing in the current session.
      // The next session will show the summary again if the save never persisted.
      this.log.error(e);
    }
  }
}
